import { useTranslation } from 'react-i18next';

import type { EnvelopeRecipientSummary, EnvelopeStatus, EnvelopeWorkflow } from '@/api/envelopes.api';

type ChainStepStatus = 'done' | 'declined' | 'current' | 'waiting';

type Props = {
  recipients: EnvelopeRecipientSummary[];
  workflowType: EnvelopeWorkflow;
  envelopeStatus: EnvelopeStatus;
  /** Compact = inline list view (single line, no labels). Default = full banner. */
  variant?: 'full' | 'compact';
  /** Highlight the step belonging to this recipient (used on SignPage). */
  highlightRecipientId?: string;
};

/**
 * Visualises a sequential signing chain — each non-CC recipient shown as a
 * numbered step with status colour, connected by lines. Strongly answers the
 * customer's "show clearly that Rahil cannot sign before Əhməd" requirement.
 * Parallel envelopes are rendered as a status row instead of a chain.
 */
export function SequentialChain({
  recipients,
  workflowType,
  envelopeStatus,
  variant = 'full',
  highlightRecipientId,
}: Props) {
  const { t } = useTranslation();

  const signers = recipients
    .filter((r) => r.role !== 'cc' && r.role !== 'for_info')
    .sort((a, b) => a.signingOrder - b.signingOrder);

  if (signers.length === 0) return null;

  const completedCount = signers.filter((r) => r.status === 'signed').length;
  const isDone = envelopeStatus === 'completed';
  const isVoided = envelopeStatus === 'voided' || envelopeStatus === 'expired';

  const stepStatus = (idx: number, r: EnvelopeRecipientSummary): ChainStepStatus => {
    if (r.status === 'signed') return 'done';
    if (r.status === 'declined') return 'declined';
    if (workflowType === 'parallel') {
      return r.status === 'viewed' || r.status === 'sent' ? 'current' : 'waiting';
    }
    const prevDone = signers
      .slice(0, idx)
      .every((p) => p.status === 'signed');
    return prevDone && !isDone && !isVoided ? 'current' : 'waiting';
  };

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {signers.map((r, idx) => (
          <CompactStep
            key={r.id}
            order={r.signingOrder}
            status={stepStatus(idx, r)}
            name={r.name}
            last={idx === signers.length - 1}
          />
        ))}
      </div>
    );
  }

  const pct = signers.length === 0 ? 0 : Math.round((completedCount / signers.length) * 100);

  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] font-semibold text-gray-900">
          {workflowType === 'sequential'
            ? t('chain.title_sequential')
            : t('chain.title_parallel')}
        </div>
        <div className="text-[11px] text-muted">
          {completedCount}/{signers.length} {t('chain.signed')}
        </div>
      </div>

      <div className="h-1 bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-success transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-start gap-1 overflow-x-auto pb-1">
        {signers.map((r, idx) => (
          <FullStep
            key={r.id}
            order={r.signingOrder}
            status={stepStatus(idx, r)}
            name={r.name}
            email={r.email}
            role={t(`recipient_role.${r.role}`, { defaultValue: r.role })}
            signedAt={r.signedAt}
            declinedAt={r.declinedAt}
            highlighted={r.id === highlightRecipientId}
            last={idx === signers.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function FullStep({
  order,
  status,
  name,
  email,
  role,
  signedAt,
  declinedAt,
  highlighted,
  last,
}: {
  order: number;
  status: ChainStepStatus;
  name: string;
  email: string;
  role: string;
  signedAt: string | null;
  declinedAt: string | null;
  highlighted: boolean;
  last: boolean;
}) {
  const { t } = useTranslation();
  const ts = signedAt || declinedAt;

  return (
    <>
      <div
        className={`flex-1 min-w-[140px] max-w-[200px] flex flex-col items-center text-center px-1 ${
          highlighted ? 'ring-2 ring-primary rounded-md py-1' : ''
        }`}
      >
        <StatusCircle status={status} order={order} />
        <div className="mt-2 text-[12px] font-medium text-gray-900 leading-tight line-clamp-2">
          {name}
        </div>
        <div className="text-[10px] text-muted truncate w-full mt-0.5">{email}</div>
        <div className="text-[9px] uppercase tracking-wide text-muted mt-0.5">{role}</div>
        {status === 'done' && ts && (
          <div className="text-[10px] text-success mt-0.5">
            ✓ {new Date(ts).toLocaleDateString()}
          </div>
        )}
        {status === 'declined' && (
          <div className="text-[10px] text-danger mt-0.5">{t('chain.declined')}</div>
        )}
        {status === 'current' && (
          <div className="text-[10px] text-warning-fg mt-0.5 font-semibold">
            ● {t('chain.waiting')}
          </div>
        )}
      </div>
      {!last && (
        <div className="self-center flex items-center px-1 pt-3">
          <div
            className={`h-0.5 w-6 ${status === 'done' ? 'bg-success' : 'bg-gray-200'}`}
          />
        </div>
      )}
    </>
  );
}

function CompactStep({
  order,
  status,
  name,
  last,
}: {
  order: number;
  status: ChainStepStatus;
  name: string;
  last: boolean;
}) {
  return (
    <span className="flex items-center gap-1">
      <span
        className={`text-[10px] rounded-full px-2 py-0.5 font-medium border ${pillClasses(status)}`}
        title={name}
      >
        #{order} {name}
      </span>
      {!last && <span className="text-muted text-[10px]">→</span>}
    </span>
  );
}

function StatusCircle({ status, order }: { status: ChainStepStatus; order: number }) {
  const base = 'w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold border-2';
  if (status === 'done') {
    return <div className={`${base} bg-success text-white border-success`}>✓</div>;
  }
  if (status === 'declined') {
    return <div className={`${base} bg-danger text-white border-danger`}>✕</div>;
  }
  if (status === 'current') {
    return (
      <div className={`${base} bg-warning-bg text-warning-fg border-warning animate-pulse`}>
        {order}
      </div>
    );
  }
  return <div className={`${base} bg-gray-50 text-muted border-gray-200`}>{order}</div>;
}

function pillClasses(status: ChainStepStatus): string {
  switch (status) {
    case 'done':
      return 'bg-success/10 text-success border-success/30';
    case 'declined':
      return 'bg-danger/10 text-danger border-danger/30';
    case 'current':
      return 'bg-warning-bg text-warning-fg border-warning/40';
    default:
      return 'bg-gray-50 text-muted border-gray-200';
  }
}
